'use client';

import type { OtherTimelineItem } from '@/lib/pr-v2/timeline-types';
import type { PrV2DiscussionResponse, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { updatePrV2State } from './fetch-pr-v2';
import { prV2UpdateStateMutationKey } from './mutation-keys';
import { prV2DiscussionQueryKey, prV2SummaryQueryKey } from './query-keys';

import {
  patchCodeSessionPullRequests,
  refetchLoadingCodeSessionPullRequests,
} from '@/data/code-sessions/patch-pull-request';
import { patchInboxPullRequest } from '@/data/pull-requests/patch-inbox-pull-request';
import { clearPendingOptimisticMutation } from '@/data/pull-requests/pending-optimistic-mutations';
import {
  inboxV2MergedPullRequestsQueryKeyPrefix,
  inboxV2OpenPullRequestsQueryKeyPrefix,
} from '@/data/pull-requests/query-keys';
import { TIMELINE_ITEM_TYPE } from '@/lib/pr-v2/timeline-types';
import { asRecord } from '@/lib/pr-v2/utils/as-record';
import { useAuth } from '@/providers/auth-provider';

type UpdateStateInput = {
  state?: 'open' | 'closed';
  draft?: boolean;
};

function describeStateUpdate({ state, draft }: UpdateStateInput): string {
  if (draft !== undefined) {
    return draft ? 'Pull request marked as draft' : 'Pull request marked ready for review';
  }
  return state === 'closed' ? 'Pull request closed' : 'Pull request reopened';
}

type MutationContext = {
  target: { owner: string; repo: string; number: string };
  rollbackSessionPullRequests: () => void;
  optimisticState?: 'open' | 'closed';
  optimisticDraft?: boolean;
  previousState?: string;
  previousDraft?: boolean;
  previousGithubUpdatedAt: string | null;
  previousMetadataUpdatedAt: string | null;
  optimisticTimelineItemId?: string;
};

function getTimelineEventType(payload: UpdateStateInput) {
  if (payload.state === 'closed') {
    return 'closed';
  }
  if (payload.state === 'open') {
    return 'reopened';
  }
  if (payload.draft === true) {
    return 'convert_to_draft';
  }
  if (payload.draft === false) {
    return 'ready_for_review';
  }
  return null;
}

function withTouchedSummary(
  current: PrV2SummaryResponse | undefined,
  payload: UpdateStateInput,
  touchedAt: string,
): PrV2SummaryResponse | undefined {
  if (!current) {
    return current;
  }

  const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
  const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};

  return {
    ...current,
    pullRequest: {
      ...current.pullRequest,
      ...(payload.state !== undefined ? { state: payload.state } : {}),
      ...(payload.draft !== undefined ? { draft: payload.draft } : {}),
      githubUpdatedAt: touchedAt,
      metadataSummary: {
        ...current.pullRequest.metadataSummary,
        updatedAt: touchedAt,
      },
      metadata: {
        ...metadataRecord,
        pullRequest: {
          ...pullRequestMetadata,
          updated_at: touchedAt,
        },
      },
    },
  };
}

function buildOptimisticTimelineItem(args: {
  id: string;
  event: NonNullable<ReturnType<typeof getTimelineEventType>>;
  touchedAt: string;
  login: string;
  avatarUrl: string;
  htmlUrl: string;
}): OtherTimelineItem {
  return {
    id: args.id,
    type: TIMELINE_ITEM_TYPE.OtherTimelineItem,
    timestamp: args.touchedAt,
    user: {
      login: args.login,
      avatar_url: args.avatarUrl,
      html_url: args.htmlUrl,
    },
    event: args.event,
    node_id: '',
    url: '',
    actor: {
      login: args.login,
      id: 0,
      node_id: '',
      avatar_url: args.avatarUrl,
      gravatar_id: null,
      url: args.htmlUrl,
      html_url: args.htmlUrl,
      followers_url: '',
      following_url: '',
      gists_url: '',
      starred_url: '',
      subscriptions_url: '',
      organizations_url: '',
      repos_url: '',
      events_url: '',
      received_events_url: '',
      type: 'User',
      site_admin: false,
      name: null,
      email: null,
    },
    commit_id: '',
    commit_url: '',
    created_at: args.touchedAt,
  } as OtherTimelineItem;
}

function appendOptimisticTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  optimisticTimelineItem: OtherTimelineItem,
): PrV2DiscussionResponse | undefined {
  if (!current) {
    return current;
  }

  if (current.timelineItems.some((item) => item.id === optimisticTimelineItem.id)) {
    return current;
  }

  return {
    ...current,
    timelineItems: [...current.timelineItems, optimisticTimelineItem],
  };
}

function removeTimelineItem(
  current: PrV2DiscussionResponse | undefined,
  timelineItemId: string,
): PrV2DiscussionResponse | undefined {
  if (!current) {
    return current;
  }

  const nextTimelineItems = current.timelineItems.filter((item) => item.id !== timelineItemId);
  if (nextTimelineItems.length === current.timelineItems.length) {
    return current;
  }

  return {
    ...current,
    timelineItems: nextTimelineItems,
  };
}

/**
 * Updates PR state/draft with optimistic summary cache writes.
 *
 * Every instance for one PR shares `prV2UpdateStateMutationKey`, so callers that must see an
 * update started from another instance can read `useIsMutating` with that key.
 */
export function usePrV2UpdateState(owner: string, repo: string, number: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const summaryKey = prV2SummaryQueryKey(owner, repo, number);
  const discussionKey = prV2DiscussionQueryKey(owner, repo, number);
  const mutationKey = prV2UpdateStateMutationKey(owner, repo, number);

  return useMutation<
    { state?: 'open' | 'closed'; draft?: boolean },
    Error,
    UpdateStateInput,
    MutationContext
  >({
    mutationKey,
    mutationFn: (payload) => updatePrV2State(owner, repo, number, payload),
    onMutate: async (payload) => {
      const touchedAt = new Date().toISOString();

      await Promise.all([
        queryClient.cancelQueries({ queryKey: summaryKey }),
        queryClient.cancelQueries({ queryKey: discussionKey }),
      ]);

      const previousSummary = queryClient.getQueryData<PrV2SummaryResponse>(summaryKey);
      const rollbackSessionPullRequests = await patchCodeSessionPullRequests(
        queryClient,
        { owner, repo, number },
        { ...payload, draft: payload.draft ?? previousSummary?.pullRequest.draft },
      );
      const eventType = getTimelineEventType(payload);
      const metadataRecord = previousSummary
        ? asRecord(previousSummary.pullRequest.metadata)
        : null;
      const pullRequestMetadata = metadataRecord ? asRecord(metadataRecord.pullRequest) : null;
      const previousMetadataUpdatedAt =
        previousSummary?.pullRequest.metadataSummary?.updatedAt ??
        (pullRequestMetadata && typeof pullRequestMetadata.updated_at === 'string'
          ? pullRequestMetadata.updated_at
          : null);
      const optimisticTimelineItemId = eventType
        ? `optimistic-state-${eventType}-${Date.now()}`
        : undefined;

      queryClient.setQueryData<PrV2SummaryResponse>(summaryKey, (current) =>
        withTouchedSummary(current, payload, touchedAt),
      );

      if (eventType && optimisticTimelineItemId) {
        const optimisticTimelineItem = buildOptimisticTimelineItem({
          id: optimisticTimelineItemId,
          event: eventType,
          touchedAt,
          login: user?.user_metadata?.user_name ?? '',
          avatarUrl: user?.user_metadata?.avatar_url ?? '',
          htmlUrl: user?.user_metadata?.html_url ?? '',
        });

        queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
          appendOptimisticTimelineItem(current, optimisticTimelineItem),
        );
      }

      patchInboxPullRequest(queryClient, { owner, repo, number: Number(number) }, (pr) => ({
        ...pr,
        ...(payload.state !== undefined ? { state: payload.state } : {}),
        ...(payload.draft !== undefined ? { draft: payload.draft } : {}),
        updatedAt: new Date().toISOString(),
      }));

      return {
        target: { owner, repo, number },
        rollbackSessionPullRequests,
        optimisticState: payload.state,
        optimisticDraft: payload.draft,
        previousState: previousSummary?.pullRequest.state,
        previousDraft: previousSummary?.pullRequest.draft,
        previousGithubUpdatedAt: previousSummary?.pullRequest.githubUpdatedAt ?? null,
        previousMetadataUpdatedAt,
        optimisticTimelineItemId,
      };
    },
    onError: (error, payload, context) => {
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to update pull request ${payload.draft !== undefined ? 'draft' : 'state'}`,
      );
      if (!context) {
        return;
      }
      const { target } = context;
      const summaryKey = prV2SummaryQueryKey(target.owner, target.repo, target.number);
      const discussionKey = prV2DiscussionQueryKey(target.owner, target.repo, target.number);
      // A failed mutation must drop the optimistic inbox lock; otherwise preserveOptimisticEntries
      // re-applies the stale snapshot over the recovery refetch for up to its 60s TTL.
      clearPendingOptimisticMutation({ ...target, number: Number(target.number) });

      context.rollbackSessionPullRequests();
      queryClient.setQueryData<PrV2SummaryResponse>(summaryKey, (current) => {
        if (!current) {
          return current;
        }

        const shouldRollbackState =
          context.optimisticState !== undefined &&
          current.pullRequest.state === context.optimisticState;
        const shouldRollbackDraft =
          context.optimisticDraft !== undefined &&
          current.pullRequest.draft === context.optimisticDraft;

        if (!shouldRollbackState && !shouldRollbackDraft) {
          return current;
        }

        const metadataRecord = asRecord(current.pullRequest.metadata) ?? {};
        const pullRequestMetadata = asRecord(metadataRecord.pullRequest) ?? {};

        return {
          ...current,
          pullRequest: {
            ...current.pullRequest,
            ...(shouldRollbackState && context.previousState !== undefined
              ? { state: context.previousState }
              : {}),
            ...(shouldRollbackDraft && context.previousDraft !== undefined
              ? { draft: context.previousDraft }
              : {}),
            githubUpdatedAt: context.previousGithubUpdatedAt,
            metadataSummary: {
              ...current.pullRequest.metadataSummary,
              updatedAt: context.previousMetadataUpdatedAt,
            },
            metadata: {
              ...metadataRecord,
              pullRequest: {
                ...pullRequestMetadata,
                updated_at: context.previousMetadataUpdatedAt,
              },
            },
          },
        };
      });

      if (context.optimisticTimelineItemId) {
        queryClient.setQueryData<PrV2DiscussionResponse>(discussionKey, (current) =>
          removeTimelineItem(current, context.optimisticTimelineItemId as string),
        );
      }
    },
    onSuccess: async ({ state, draft }, payload, context) => {
      const { target } = context;
      const summaryKey = prV2SummaryQueryKey(target.owner, target.repo, target.number);
      toast.success(describeStateUpdate(payload));
      const touchedAt = new Date().toISOString();
      queryClient.setQueryData<PrV2SummaryResponse>(summaryKey, (current) => {
        return withTouchedSummary(current, { state, draft }, touchedAt);
      });
      await patchCodeSessionPullRequests(queryClient, target, { state, draft });
      await refetchLoadingCodeSessionPullRequests(queryClient);
    },
    onSettled: async (_data, _error, _payload, context) => {
      if (!context) {
        return;
      }
      const { target } = context;
      const summaryKey = prV2SummaryQueryKey(target.owner, target.repo, target.number);
      const discussionKey = prV2DiscussionQueryKey(target.owner, target.repo, target.number);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: summaryKey }),
        queryClient.invalidateQueries({ queryKey: discussionKey }),
        queryClient.invalidateQueries({ queryKey: inboxV2OpenPullRequestsQueryKeyPrefix() }),
        queryClient.invalidateQueries({ queryKey: inboxV2MergedPullRequestsQueryKeyPrefix() }),
      ]);
    },
  });
}
